/**
 * ReviewRail — right-side rail (320 px) in the meeting studio layout.
 *
 * Contains (in order):
 *   1. ReviewPanel (flags/drift/suggestions) — hidden when locked
 *   2. ProposedAttendeesCard — hidden when locked
 *   3. ProposedGlossaryCard — hidden when locked
 *   4. <details> context disclosure — always visible when context present
 *      (locked meetings keep their context viewable)
 *
 * Collapse state persisted in localStorage as "timlul-rail".
 * Below 1280px the rail becomes a slide-over toggled by the header's
 * "Review" button (the parent owns `mobileOpen`); layout properties live
 * in global.css under .review-rail so the breakpoint can re-position it.
 */

import { type Ref, useEffect, useRef } from "react"; // Ref used by reviewPanelRef prop type
import { IconButton } from "../../ui/IconButton";
import { ReviewPanel, ReviewPanelHandle } from "../../components/ReviewPanel";
import { ProposedAttendeesCard, ProposedAttendee } from "../../components/ProposedAttendeesCard";
import { ProposedGlossaryCard, ProposedTerm } from "../../components/ProposedGlossaryCard";
import { t } from "../../i18n";
import { ReviewItem } from "../../lib/reviewItems";
import { Flag } from "../../lib/flags";

const RAIL_STORAGE_KEY = "timlul-rail";

interface SonioxContext {
  general?: { key: string; value: string }[];
  terms?: string[];
  text?: string;
}

interface Props {
  // Rail visibility
  collapsed: boolean;
  onToggleCollapse: () => void;
  // Below 1280px the rail is a slide-over; the parent owns the open state
  // (toggled by the header's "Review" button).
  mobileOpen: boolean;
  onMobileClose: () => void;

  // ReviewPanel props
  reviewItems: ReviewItem[];
  recordedAt: string | null;
  isLocked: boolean;
  meetingStatus: string;
  meetingId: string;
  workspaceId: string;
  hasAnnotationRun: boolean;
  flags: Flag[];
  reviewPanelRef: Ref<ReviewPanelHandle>;
  onSeek: (ms: number) => void;
  getOriginalText: (startMs: number, endMs: number) => string;
  onEditSpeaker: (ms: number) => void;
  onActed: () => Promise<void>;
  onReAnnotate: () => void;
  canReAnnotate: boolean;
  reannotating: boolean;

  // ProposedCards props
  displayAttendees: ProposedAttendee[];
  displayTerms: ProposedTerm[];
  meetingProjectId: string | null;
  onEditAttendee: (idx: number, patch: Partial<ProposedAttendee>) => void;
  onRemoveAttendee: (idx: number) => void;
  onEditTerm: (idx: number, patch: Partial<ProposedTerm>) => void;
  onRemoveTerm: (idx: number) => void;
  onToast: (msg: string, undoFn?: () => void) => void;
  onReinsertAttendee: (a: ProposedAttendee) => void;
  onReinsertTerm: (t: ProposedTerm) => void;

  // Context
  prose: string;
  context: SonioxContext | null;
}

function StructuredContextSection({ context }: { context: SonioxContext }) {
  const general = context.general ?? [];
  const terms = context.terms ?? [];
  const text = context.text ?? "";
  return (
    <>
      {general.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
            Details
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <tbody>
              {general.map((entry, i) => (
                <tr key={i}>
                  <td style={{ padding: "3px 10px 3px 0", color: "var(--text-muted)", fontFamily: "monospace", whiteSpace: "nowrap", verticalAlign: "top" }}>
                    {entry.key}
                  </td>
                  <td dir="auto" style={{ padding: "3px 0", fontSize: 13 }}>{entry.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {terms.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
            Names and terms
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {terms.map((term, i) => (
              <span
                key={i}
                dir="auto"
                style={{
                  display: "inline-block",
                  padding: "2px 7px",
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 12,
                  fontFamily: "monospace",
                }}
              >
                {term}
              </span>
            ))}
          </div>
        </div>
      )}
      {text && (
        <div>
          {/* This is the SYSTEM-composed briefing sent with the audio — not the
              user's raw notes (those render above under "Your notes"). The two
              had the same label once, which read as a duplicate. */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
            {t("meeting.contextBriefing")}
          </div>
          <div dir="auto" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{text}</div>
        </div>
      )}
    </>
  );
}

export function ReviewRail({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
  reviewItems,
  recordedAt,
  isLocked,
  meetingStatus,
  meetingId,
  workspaceId,
  hasAnnotationRun,
  reviewPanelRef,
  onSeek,
  getOriginalText,
  onEditSpeaker,
  onActed,
  onReAnnotate,
  canReAnnotate,
  reannotating,
  displayAttendees,
  displayTerms,
  meetingProjectId,
  onEditAttendee,
  onRemoveAttendee,
  onEditTerm,
  onRemoveTerm,
  onToast,
  onReinsertAttendee,
  onReinsertTerm,
  prose,
  context,
}: Props) {
  const hasContext =
    !!prose ||
    !!(context && (context.general?.length || context.terms?.length || context.text));

  // Ref to the aside element, used to find the close button on open.
  const railRef = useRef<HTMLElement | null>(null);

  // Esc-to-close the mobile slide-over + focus the close button on open.
  useEffect(() => {
    if (!mobileOpen) return;
    // Focus the mobile-close button for keyboard accessibility.
    const closeBtn = railRef.current?.querySelector<HTMLButtonElement>(".review-rail-mobile-close");
    closeBtn?.focus();
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onMobileClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [mobileOpen, onMobileClose]);

  // Desktop collapsed strip. On mobile the strip is hidden by CSS and the
  // slide-over (below) takes over whenever the parent opens it.
  if (collapsed && !mobileOpen) {
    return (
      <aside data-testid="review-rail" className="review-rail review-rail-collapsed">
        <IconButton
          label="Expand review rail"
          name="chevron-left"
          size={16}
          onClick={onToggleCollapse}
          title="Expand review panel"
        />
      </aside>
    );
  }

  return (
    <>
      {/* Mobile slide-over backdrop — click closes. display:none on desktop. */}
      {mobileOpen && (
        <button
          type="button"
          className="review-rail-backdrop"
          aria-label="Close review panel"
          onClick={onMobileClose}
        />
      )}
      <aside
        ref={railRef}
        data-testid="review-rail"
        className={`review-rail${mobileOpen ? " is-mobile-open" : ""}`}
      >
      {/* Rail heading + collapse (desktop) / close (mobile) buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{t("meeting.reviewRail")}</span>
        <span style={{ display: "inline-flex", gap: 4 }}>
          <IconButton
            label="Collapse review rail"
            name="chevron-right"
            size={16}
            onClick={onToggleCollapse}
            title="Collapse review panel"
            className="review-rail-desktop-collapse"
          />
          <IconButton
            label="Close review panel"
            name="x"
            size={16}
            onClick={onMobileClose}
            className="review-rail-mobile-close"
          />
        </span>
      </div>

      {/* ReviewPanel — hidden when locked */}
      {!isLocked && (
        <ReviewPanel
          ref={reviewPanelRef}
          items={reviewItems}
          recordedAt={recordedAt}
          isLocked={isLocked}
          meetingStatus={meetingStatus}
          meetingId={meetingId}
          workspaceId={workspaceId}
          hasAnnotationRun={hasAnnotationRun}
          onSeek={onSeek}
          getOriginalText={getOriginalText}
          onEditSpeaker={onEditSpeaker}
          onActed={onActed}
          onReAnnotate={onReAnnotate}
          canReAnnotate={canReAnnotate}
          reAnnotating={reannotating}
        />
      )}

      {/* ProposedAttendeesCard — read-only when locked, hidden when locked AND empty */}
      {displayAttendees.length > 0 && (
        <ProposedAttendeesCard
          attendees={displayAttendees}
          workspaceId={workspaceId}
          meetingProjectId={meetingProjectId}
          editable={!isLocked}
          onEdit={onEditAttendee}
          onRemove={onRemoveAttendee}
          onToast={(msg, undoFn) => onToast(msg, undoFn)}
          onReinsert={onReinsertAttendee}
        />
      )}

      {/* ProposedGlossaryCard — read-only when locked, hidden when locked AND empty */}
      {displayTerms.length > 0 && (
        <ProposedGlossaryCard
          terms={displayTerms}
          workspaceId={workspaceId}
          meetingProjectId={meetingProjectId}
          editable={!isLocked}
          onEdit={onEditTerm}
          onRemove={onRemoveTerm}
          onToast={(msg, undoFn) => onToast(msg, undoFn)}
          onReinsert={onReinsertTerm}
        />
      )}

      {/* Honest empty state: with no proposals at all, an empty rail reads as
          "feature missing" rather than "nothing found" (a user asked exactly
          that). Only for unlocked meetings — locked ones are deliberately quiet. */}
      {!isLocked && displayAttendees.length === 0 && displayTerms.length === 0 && (
        <div
          data-testid="no-proposals-note"
          style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 2px" }}
        >
          {t("meeting.noProposals")}
        </div>
      )}

      {/* Context disclosure — always visible when context present */}
      {hasContext && (
        <details data-testid="context-disclosure" open>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--text-muted)",
              userSelect: "none",
              paddingBlock: 6,
              listStyle: "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t("meeting.contextDisclosure")}
          </summary>
          <div style={{ marginTop: 10 }}>
            {prose && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
                  Your notes
                </div>
                <div dir="auto" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{prose}</div>
              </div>
            )}
            {context && (context.general?.length || context.terms?.length || context.text) ? (
              <StructuredContextSection context={context} />
            ) : null}
          </div>
        </details>
      )}
      </aside>
    </>
  );
}

/** Read / write the collapsed state from localStorage. */
export function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "collapsed";
  } catch {
    return false;
  }
}

export function writeRailCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) {
      localStorage.setItem(RAIL_STORAGE_KEY, "collapsed");
    } else {
      localStorage.removeItem(RAIL_STORAGE_KEY);
    }
  } catch { /* best-effort */ }
}
